import * as React from 'react';
import type { ReactNode } from 'react';
import { cx } from '../utils';
import type { StyledProps, WithChildren } from '../types';
import type { UnsafeStyleProps } from '../escape-hatches';

/* Bounded rest-prop allow-list typed via React.HTMLAttributes so
   adopters wrapping Label don't hit unknown-type collisions when
   composing (see codex SectionHeader r1 lesson). */
type AllowedRest = Pick<React.HTMLAttributes<HTMLElement>, 'id' | 'title' | 'lang' | 'dir'> & {
  [key: `data-${string}`]: string | number | boolean | undefined;
  [key: `aria-${string}`]: string | boolean | number | undefined;
};
const ALLOWED_LITERAL_KEYS = new Set<string>(['id', 'title', 'lang', 'dir']);
function pickAllowed(rest: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(rest)) {
    if (ALLOWED_LITERAL_KEYS.has(key) || key.startsWith('data-') || key.startsWith('aria-')) {
      out[key] = rest[key];
    }
  }
  return out;
}

export interface LabelProps extends StyledProps, WithChildren, UnsafeStyleProps, AllowedRest {
  /* When set, renders `<label for={htmlFor}>` for native form-label
     semantics (browser focuses the associated control on click). When
     absent, renders `<span>` for visual-only labeling (adopter wires
     aria-labelledby manually). */
  htmlFor?: string;
  /* Renders an asterisk indicator with `aria-hidden="true"` plus an
     sr-only `(required)` text so AT users get the required signal
     without color-only conveyance. */
  required?: boolean;
}

/* SR-only style applied inline so the kit doesn't depend on a global
   `.sr-only` utility class being defined in adopter CSS. */
const SR_ONLY_STYLE: React.CSSProperties = {
  position: 'absolute',
  inlineSize: 1,
  blockSize: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
  border: 0,
};

export const Label = React.forwardRef<HTMLElement, LabelProps>(function Label(
  { children, htmlFor, required, className, unsafe_style, ...rest },
  ref,
) {
  const restProps = pickAllowed(
    rest as Record<string, unknown>,
  ) as React.HTMLAttributes<HTMLElement>;

  const requiredIndicator = required ? (
    <>
      {' '}
      <span className="j-label-required" aria-hidden="true">
        *
      </span>
      <span style={SR_ONLY_STYLE}> (required)</span>
    </>
  ) : null;

  const content: ReactNode = (
    <>
      {children}
      {requiredIndicator}
    </>
  );

  if (htmlFor) {
    return (
      <label
        ref={ref as React.Ref<HTMLLabelElement>}
        {...restProps}
        className={cx('j-label', className)}
        style={unsafe_style}
        htmlFor={htmlFor}
      >
        {content}
      </label>
    );
  }

  return (
    <span
      ref={ref as React.Ref<HTMLSpanElement>}
      {...restProps}
      className={cx('j-label', className)}
      style={unsafe_style}
    >
      {content}
    </span>
  );
});
